import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  faCircleExclamation,
  faRotate,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { List, ListItem } from '@/components/List';
import { Typography } from '@/components/Typography';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import authService from '@/data/services/auth.service';

interface AuthErrorDisplayProps {
  error: {
    message: string;
    status: number;
    timestamp: number;
    origin?: {
      url?: string;
      method?: string;
      baseURL?: string;
    };
  };
  isDevelopment?: boolean;
  onRetry?: () => void;
}

export const AuthErrorDisplay: React.FC<AuthErrorDisplayProps> = ({
  error,
  isDevelopment = false,
  onRetry,
}) => {
  const { t } = useTranslation();

  const getErrorTitle = () => {
    switch (error.status) {
      case 401:
        return t('auth.error.401');
      case 403:
        return t('auth.error.403');
      case 404:
        return t('auth.error.404');
      case 500:
      case 502:
      case 503:
        return t('auth.error.500');
      default:
        return t('auth.error.default');
    }
  };

  const getErrorReasons = () => {
    const baseReasons = [
      t('auth.error.scud.token'),
      t('auth.error.scud.permission'),
      t('auth.error.scud.unavailable'),
      t('auth.error.reason.network'),
    ];

    if (error.status === 403) {
      return [
        t('auth.error.scud.permission'),
        t('auth.error.unauthorized'),
        t('auth.error.scud.permission_changed'),
      ];
    }

    if (error.status >= 500) {
      return [
        t('auth.error.scud.api'),
        t('auth.error.mainetanance'),
        t('auth.error.interuption'),
      ];
    }

    return baseReasons;
  };

  const handleRetry = () => {
    authService.clearAllAuthData();
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className='flex min-h-svh flex-col items-center justify-center gap-4 p-4'>
      <div className='w-full max-w-xl rounded-xl border border-t-4 border-t-destructive bg-card p-6'>
        <div className='mb-4 flex items-center gap-4'>
          <FontAwesomeIcon icon={faCircleExclamation} className='size-8 text-destructive' />
          <div className='flex flex-col items-start gap-2'>
            <Typography variant='h4' as='h1'>
              {getErrorTitle()}
            </Typography>
            {error.status > 0 && (
              <Badge variant='outline' className='border-destructive text-destructive'>
                {`Error ${error.status}`}
              </Badge>
            )}
          </div>
        </div>

        <Alert variant='destructive' className='mb-6'>
          <AlertDescription>
            <Typography variant='paragraphSmall' color='destructive'>
              <Typography variant='paragraphSmallBold' color='destructive' as='span'>
                {t('common.details')} :
              </Typography> {error.message}
            </Typography>
            {error.origin && (
              <Typography variant='paragraphMini' color='destructive' className='mt-1'>
                <Typography variant='paragraphMiniBold' color='destructive' as='span'>
                  Origin:
                </Typography> {error.origin.method} {error.origin.baseURL}
                {error.origin.url}
              </Typography>
            )}
          </AlertDescription>
        </Alert>

        <Typography variant='h4' className='mb-2'>
          {t('common.causes')} :
        </Typography>

        <List className='mb-6 flex flex-col gap-2'>
          {getErrorReasons().map((reason, index) => (
            <ListItem key={index} className='flex items-center gap-3'>
              <FontAwesomeIcon icon={faTriangleExclamation} className='size-4 text-warning' />
              <Typography variant='paragraphSmall'>{reason}</Typography>
            </ListItem>
          ))}
        </List>

        <Button className='w-full' onClick={handleRetry}>
          <FontAwesomeIcon icon={faRotate} />
          {t('auth.error.try_again')}
        </Button>

        {isDevelopment && (
          <Typography variant='paragraphMini' className='mt-4 text-center'>
            {t('auth.error.development_mode', {
              code: 'VITE_APP_SKIP_AUTH=true',
            })}
          </Typography>
        )}
      </div>

      <Typography variant='paragraphMini'>
        {t('auth.error.id_request')} :{' '}
        {new Date(error.timestamp).toLocaleDateString('fr-FR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })}
      </Typography>
    </div>
  );
};
